// MIT License
//
// Copyright (c) 2021 heo@mapiacompany.com
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

declare const jQuery: any;

/**
 * Slick component
 * Based on https://github.com/leo6104/ngx-slick-carousel
 */
@Component({
  selector: 'app-slick-carousel',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SlickCarouselComponent),
    multi: true,
  }],
  template: '<ng-content></ng-content>',
  standalone: false,
})
export class SlickCarouselComponent implements OnDestroy, OnChanges, AfterViewInit, AfterViewChecked {

  @Input() config: any;
  @Output() afterChange: EventEmitter<{event: any, slick: any, currentSlide: number, first: boolean, last: boolean}> = new EventEmitter();
  @Output() beforeChange: EventEmitter<{event: any, slick: any, currentSlide: number, nextSlide: number}> = new EventEmitter();
  @Output() breakpoint: EventEmitter<{event: any, slick: any, breakpoint: any}> = new EventEmitter();
  @Output() destroy: EventEmitter<{event: any, slick: any}> = new EventEmitter();
  @Output() init: EventEmitter<{event: any, slick: any}> = new EventEmitter();

  public $instance: any;

  // access from parent component can be a problem with change detection timing. Please use afterChange output
  private currentIndex: number = 0;

  public slides: any[] = [];
  public initialized: boolean = false;
  private removedSlides: SlickItemDirective[] = [];
  private addedSlides: SlickItemDirective[] = [];

  private readonly el: ElementRef = inject(ElementRef);
  private readonly zone: NgZone = inject(NgZone);
  private readonly platformId: string = inject(PLATFORM_ID) as string;

  /**
   * On component destroy
   */
  ngOnDestroy(): void {
    this.unslick();
  }

  ngAfterViewInit(): void {
    this.ngAfterViewChecked();
  }

  /**
   * On component view checked
   */
  ngAfterViewChecked(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    if (this.addedSlides.length > 0 || this.removedSlides.length > 0) {
      const nextSlidesLength: number = this.slides.length - this.removedSlides.length + this.addedSlides.length;
      if (!this.initialized) {
        if (nextSlidesLength > 0) {
          this.initSlick();
        }
        // if nextSlidesLength is zere, do nothing
      } else if (nextSlidesLength === 0) { // unslick case
        this.unslick();
      } else {
        this.addedSlides.forEach(slickItem => {
          this.slides.push(slickItem);
          this.zone.runOutsideAngular(() => {
            this.$instance.slick('slickAdd', slickItem.el.nativeElement);
          });
        });
        this.addedSlides = [];

        this.removedSlides.forEach(slickItem => {
          const idx: number = this.slides.indexOf(slickItem);
          this.slides = this.slides.filter(s => s !== slickItem);
          this.zone.runOutsideAngular(() => {
            this.$instance.slick('slickRemove', idx);
          });
        });
        this.removedSlides = [];
      }
    }
  }

  /**
   * init slick
   */
  initSlick(): void {
    this.slides = this.addedSlides;
    this.addedSlides = [];
    this.removedSlides = [];
    this.zone.runOutsideAngular(() => {
      this.$instance = jQuery(this.el.nativeElement);

      this.$instance.on('init', (event: any, slick: any) => {
        this.zone.run(() => {
          this.init.emit({event, slick});
        });
      });

      this.$instance.slick(this.config);

      this.zone.run(() => {
        this.initialized = true;

        this.currentIndex = this.config?.initialSlide || 0;
      });

      this.$instance.on('afterChange', (event: any, slick: any, currentSlide: any) => {
        this.zone.run(() => {
          this.afterChange.emit({
            event,
            slick,
            currentSlide,
            first: currentSlide === 0,
            last: slick.$slides.length === currentSlide + slick.options.slidesToScroll,
          });
          this.currentIndex = currentSlide;
        });
      });

      this.$instance.on('beforeChange', (event: any, slick: any, currentSlide: any, nextSlide: any) => {
        this.zone.run(() => {
          this.beforeChange.emit({event, slick, currentSlide, nextSlide});
          this.currentIndex = nextSlide;
        });
      });

      this.$instance.on('breakpoint', (event: any, slick: any, breakpoint: any) => {
        this.zone.run(() => {
          this.breakpoint.emit({event, slick, breakpoint});
        });
      });

      this.$instance.on('destroy', (event: any, slick: any) => {
        this.zone.run(() => {
          this.destroy.emit({event, slick});
          this.initialized = false;
        });
      });
    });
  }

  addSlide(slickItem: SlickItemDirective): void {
    this.addedSlides.push(slickItem);
  }

  removeSlide(slickItem: SlickItemDirective): void {
    this.removedSlides.push(slickItem);
  }

  /**
   * Slick Method
   */
  public slickGoTo(index: number): void {
    this.zone.runOutsideAngular(() => {
      this.$instance.slick('slickGoTo', index);
    });
  }

  public slickNext(): void {
    this.zone.runOutsideAngular(() => {
      this.$instance.slick('slickNext');
    });
  }

  public slickPrev(): void {
    this.zone.runOutsideAngular(() => {
      this.$instance.slick('slickPrev');
    });
  }

  public slickPause(): void {
    this.zone.runOutsideAngular(() => {
      this.$instance.slick('slickPause');
    });
  }

  public slickPlay(): void {
    this.zone.runOutsideAngular(() => {
      this.$instance.slick('slickPlay');
    });
  }

  public unslick(): void {
    if (this.$instance) {
      this.zone.runOutsideAngular(() => {
        this.$instance.slick('unslick');
      });
      this.$instance = undefined;
    }
    this.initialized = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized) {
      const config: SimpleChange = changes.config;
      if (config.previousValue !== config.currentValue && config.currentValue !== undefined) {
        const refresh: boolean = config.currentValue.refresh;
        const newOptions: Record<string, any> = Object.assign({}, config.currentValue);
        delete newOptions.refresh;

        this.zone.runOutsideAngular(() => {
          this.$instance.slick('slickSetOption', newOptions, refresh);
        });
      }
    }
  }

}

@Directive({
  selector: '[appSlickItem]',
  standalone: false,
})
export class SlickItemDirective implements OnInit, OnDestroy {
  public el: ElementRef = inject(ElementRef);
  private readonly platformId: string = inject(PLATFORM_ID) as string;
  private readonly carousel: SlickCarouselComponent = inject(SlickCarouselComponent);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.carousel.addSlide(this);
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.carousel.removeSlide(this);
    }
  }
}
